import { Tabs } from "@newsela/angelou";
import { useEffect, useMemo, useState } from "react";

import { useStandardsModalContext } from "@/search/Langston/FilterPanel/filters/Standards/StandardsModalProvider";
import { useStandardsSearchContext } from "@/search/Langston/StandardsSearchProvider";
import { STANDARDS_SET_MODE } from "@/search/Langston/types/IStandardsSetMode";
import { AllStandardsList } from "./AllStandardsList";
import { CreateStandardsSets } from "./CreateStandardsSets";
import { EditableStandardsSet } from "./EditableStandardsSet";
import {
  tabListCss,
  tabPanelAdjustmentsCss,
  tabPanelsCss,
} from "./StandardsModalTabs.styles";
import { useStandardsModalActiveTab } from "./hooks";

interface StandardsModalTabsProps {
  selectionLimit?: number | undefined;
  canCreateOrEdit?: boolean | undefined;
}

export const StandardsModalTabs = ({
  selectionLimit,
  canCreateOrEdit = true,
}: StandardsModalTabsProps) => {
  const {
    getters: {
      createdStandardsSets,
      isEditingSet,
      modalTabs,
      draftStandardsSetId,
    },
    handlers: {
      setStandardsSetMode,
      switchEditDataOnTabChange,
      setIsEditingSet,
      setModalTabs,
      handleCreateDraftStandardsSet,
      setAllowModalTabAutoChanges,
    },
  } = useStandardsModalContext();

  const { isLoadingStandardsResults, resetStandardsSearch, selectedStandards } =
    useStandardsSearchContext();

  const tabsClass = "standards-modal-tabs";
  const activeTab = useStandardsModalActiveTab(tabsClass);
  const [prevActiveTab, setPrevActiveTab] = useState<string | undefined>();

  const selectedStandardIds = selectedStandards.map(({ id }) => id);
  const [temporarySelectedIds, setTemporarySelectedIds] =
    useState<string[]>(selectedStandardIds);
  const limit = selectionLimit
    ? {
        count: selectionLimit,
        temporarySelectedIds,
        setTemporarySelectedIds,
      }
    : undefined;

  const dynamicStandardsSetsTabs = useMemo(
    () =>
      createdStandardsSets.map((standardsSet) => ({
        name: standardsSet.title,
        panelId: standardsSet.id.toString(),
        panelContent: (
          <EditableStandardsSet
            standardsSet={standardsSet}
            limit={limit}
            canCreateOrEdit={canCreateOrEdit}
          />
        ),
      })),

    [createdStandardsSets, limit],
  );

  useEffect(() => {
    const activeTabTitle = activeTab?.innerText.trim();
    if (
      activeTabTitle &&
      (!activeTabTitle?.toLowerCase().includes("create") ||
        !activeTabTitle?.toLowerCase().includes("all"))
    ) {
      setIsEditingSet(false);
      switchEditDataOnTabChange(activeTab);
    }
    if (activeTabTitle?.toLowerCase().includes("create")) {
      setStandardsSetMode(STANDARDS_SET_MODE.CREATE);
      handleCreateDraftStandardsSet();
    } else if (!isEditingSet) {
      setStandardsSetMode(STANDARDS_SET_MODE.VIEW);
    }
    if (activeTabTitle !== prevActiveTab) {
      if (prevActiveTab?.includes("all")) {
        setAllowModalTabAutoChanges(true);
      }
      setPrevActiveTab(activeTabTitle?.toLowerCase());
    }
  }, [activeTab]);

  useEffect(() => {
    if (
      prevActiveTab &&
      (prevActiveTab.includes("all") || prevActiveTab.includes("create"))
    ) {
      const inputField = document.getElementById(
        "standards-modal-search-input",
      );
      if (inputField) {
        (inputField as HTMLInputElement).value = "";
      }
      resetStandardsSearch();
    }
  }, [prevActiveTab]);

  useEffect(() => {
    setModalTabs([
      {
        name: "All",
        panelId: "all",
        panelContent: <AllStandardsList limit={limit} />,
      },
      ...dynamicStandardsSetsTabs,
      ...(canCreateOrEdit
        ? [
            {
              name: "+ Create Standards Set",
              panelId: "createStandardsSet",
              panelContent: <CreateStandardsSets />,
            },
          ]
        : []),
    ]);
  }, [
    createdStandardsSets.length,
    limit,
    dynamicStandardsSetsTabs,
    draftStandardsSetId,
    canCreateOrEdit,
  ]);

  if (!modalTabs || modalTabs[0]?.panelId !== "all") {
    return null;
  }

  return (
    <Tabs
      __classNameFor={{
        tabList: tabsClass,
      }}
      horizontalSwitch="sm"
      initialPanelId={modalTabs[0]?.panelId}
      renderTabsAsButtons
      flavor="underlined"
      __cssFor={{
        root: tabPanelAdjustmentsCss,
        tabList: tabListCss(isLoadingStandardsResults),
        tabPanels: tabPanelsCss,
      }}
      panels={modalTabs}
      dataProps={{
        "data-testid": "standards-modal-tabs",
        "data-qa-selector": "standards-modal-tabs",
      }}
    />
  );
};
